import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import styled from "styled-components";

export interface SignaturePadHandle {
  clear(): void;
  isEmpty(): boolean;
  /** PNG com fundo transparente, pronto para o campo image_base64. */
  toDataURL(): string | null;
}

interface SignaturePadProps {
  height?: number;
  disabled?: boolean;
  /** Avisa a tela quando o traço deixa de estar vazio (ou volta a ficar). */
  onDrawnChange?: (hasDrawing: boolean) => void;
}

const Canvas = styled.canvas<{ $disabled?: boolean }>`
  width: 100%;
  display: block;
  border: 1px dashed var(--border);
  border-radius: 8px;
  background: #ffffff;
  /* Sem isto o navegador rola a página em vez de deixar desenhar no toque. */
  touch-action: none;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "crosshair")};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
`;

/**
 * Área de desenho livre para colher assinatura com dedo ou mouse. Guarda o
 * traço no próprio canvas e devolve PNG transparente — nada de biblioteca
 * externa, e o mesmo componente serve no desktop e no celular.
 */
const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ height = 180, disabled = false, onDrawnChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef(false);
    const [hasDrawing, setHasDrawing] = useState(false);

    /**
     * O canvas precisa de resolução real em pixels, senão o traço sai
     * borrado em tela de alta densidade. Redimensionar limpa o conteúdo, por
     * isso o traço é preservado e redesenhado.
     */
    const resize = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      if (!width) return;

      const previous =
        canvas.width && canvas.height ? canvas.toDataURL("image/png") : null;

      canvas.width = width * ratio;
      canvas.height = height * ratio;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1a1a1a";

      if (previous) {
        const image = new Image();
        image.onload = () => ctx.drawImage(image, 0, 0, width, height);
        image.src = previous;
      }
    }, [height]);

    useEffect(() => {
      resize();
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }, [resize]);

    const markDrawn = (value: boolean) => {
      setHasDrawing(value);
      onDrawnChange?.(value);
    };

    const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;

      event.currentTarget.setPointerCapture(event.pointerId);
      drawingRef.current = true;

      const { x, y } = pointFrom(event);
      ctx.beginPath();
      ctx.moveTo(x, y);
      // Um toque simples já vale como assinatura (um ponto).
      ctx.lineTo(x + 0.01, y);
      ctx.stroke();
      if (!hasDrawing) markDrawn(true);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current || disabled) return;
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;

      const { x, y } = pointFrom(event);
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* o ponteiro já pode ter sido liberado pelo navegador */
      }
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        markDrawn(false);
      },
      isEmpty: () => !hasDrawing,
      toDataURL: () =>
        hasDrawing ? canvasRef.current?.toDataURL("image/png") ?? null : null
    }));

    return (
      <Canvas
        ref={canvasRef}
        $disabled={disabled}
        style={{ height }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
    );
  }
);

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
